import { useEffect, useState, useCallback, useRef } from 'preact/hooks'

/**
 * Ruft `aktion` auf, sobald die Seite wieder nach vorn kommt.
 *
 * Zwei Wege führen dorthin, und die App braucht beide:
 *   - `visibilitychange`: die installierte PWA lag im Hintergrund, während
 *     woanders (oder auf einem anderen Gerät) gelernt wurde.
 *   - `pageshow` mit `persisted`: Safari holt die Seite beim Zurückwischen aus
 *     dem Vor-/Zurück-Puffer — dabei läuft kein Effekt erneut, der Bildschirm
 *     stünde sonst unverändert da.
 *
 * Die Aktion liegt in einer Ref, damit die Zuhörer genau einmal registriert
 * werden und trotzdem immer die frische Fassung aufrufen.
 */
export function useBeiRueckkehr(aktion) {
  const gemerkt = useRef(aktion)
  gemerkt.current = aktion

  useEffect(() => {
    function beiSichtbarkeit() {
      if (document.visibilityState === 'visible') gemerkt.current()
    }
    function beiSeitenanzeige(ereignis) {
      if (ereignis.persisted) gemerkt.current()
    }
    document.addEventListener('visibilitychange', beiSichtbarkeit)
    window.addEventListener('pageshow', beiSeitenanzeige)
    return () => {
      document.removeEventListener('visibilitychange', beiSichtbarkeit)
      window.removeEventListener('pageshow', beiSeitenanzeige)
    }
  }, [])
}

/**
 * Lädt einmalig und stellt Laden, Fehler und Neuladen bereit.
 * Bewusst klein gehalten: sechs Bildschirme brauchen keinen Zustandsspeicher.
 *
 * Bei der Rückkehr aus dem Hintergrund wird still nachgeladen: ohne Ladebalken
 * und ohne die alten Zahlen wegzunehmen. Die App bleibt dadurch stehen, wo sie
 * stand, zeigt aber den Stand von jetzt.
 */
export function useLaden(fn, abhaengigkeiten = []) {
  const [zustand, setZustand] = useState({ daten: null, laedt: true, fehler: null })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const laden = useCallback(fn, abhaengigkeiten)

  const [runde, setRunde] = useState(0)
  const neuLaden = useCallback(() => setRunde((r) => r + 1), [])

  const lebt = useRef(true)
  useEffect(() => {
    lebt.current = true
    return () => {
      lebt.current = false
    }
  }, [])

  useEffect(() => {
    let abgebrochen = false
    setZustand((z) => ({ ...z, laedt: true, fehler: null }))
    laden()
      .then((daten) => {
        if (!abgebrochen) setZustand({ daten, laedt: false, fehler: null })
      })
      .catch((fehler) => {
        if (!abgebrochen) setZustand({ daten: null, laedt: false, fehler })
      })
    return () => {
      abgebrochen = true
    }
  }, [laden, runde])

  useBeiRueckkehr(() => {
    laden()
      .then((daten) => {
        if (lebt.current) setZustand({ daten, laedt: false, fehler: null })
      })
      .catch(() => {
        // Stiller Fehlschlag mit Absicht: eine kurz weggebrochene Verbindung
        // darf einen dastehenden Bildschirm nicht durch eine Fehlermeldung
        // ersetzen. Der nächste Vordergrundwechsel versucht es erneut.
      })
  })

  return { ...zustand, neuLaden }
}
